/**
 * Reading spreadsheets — .xlsx, .xls and CSV — through one door.
 *
 * Every importer in this app works on CSV text. Rather than teach each of them
 * a second format, an Excel file is converted to CSV text here and the existing
 * parsers carry on unchanged. That also means a fix to date or delimiter
 * handling lands for all three importers at once.
 *
 * SheetJS is ~430KB, so it is loaded on demand and only when someone actually
 * opens an .xlsx. It is the single heaviest thing this app can fetch, so the
 * load goes through loadChunk() — see src/lib/lazyChunk.ts for what can go
 * wrong with an on-demand library and which of it a retry can fix.
 */

import { loadChunk } from '@/lib/lazyChunk';

/** SheetJS, loaded once per session. */
let xlsxPromise: Promise<typeof import('xlsx')> | null = null;

export async function loadXlsx(): Promise<typeof import('xlsx')> {
  if (!xlsxPromise) {
    xlsxPromise = loadChunk(
      () => import('xlsx'),
      'Excel',
      'Atau pakai format CSV, yang tidak perlu modul ini.',
    ).catch((e) => {
      xlsxPromise = null; // let the next attempt start fresh
      throw e;
    });
  }
  return xlsxPromise;
}

const EXCEL_EXT = /\.(xlsx|xlsm|xlsb|xls)$/i;

export const isExcelFile = (file: File): boolean =>
  EXCEL_EXT.test(file.name) ||
  file.type.includes('spreadsheetml') ||
  file.type === 'application/vnd.ms-excel';

/** What every import dialog accepts. Excel first — it is what people have. */
export const IMPORT_ACCEPT =
  '.xlsx,.xls,.xlsm,.csv,text/csv,text/plain,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

/**
 * File → CSV text, whatever the file was.
 *
 * Only the first sheet is read. A workbook with the data on sheet two is rare
 * enough, and guessing which sheet was meant is worse than saying so.
 */
export async function spreadsheetFileToText(file: File): Promise<string> {
  if (!isExcelFile(file)) return file.text();

  const XLSX = await loadXlsx();
  const wb = XLSX.read(await file.arrayBuffer(), {
    type: 'array',
    // A real date cell renders in the sheet's own display format, which is the
    // operator's locale — 31/12/2027 here, 12/31/2027 on a US machine. The
    // second is refused by parseSheetDate as ambiguous, and rightly so. Forcing
    // ISO on the way out removes the guess entirely: an expiry typed as a date
    // in Excel arrives as 2027-12-31 no matter whose laptop it was typed on.
    cellDates: true,
    dateNF: 'yyyy-mm-dd',
    raw: false,
  });

  // The stock template and "Export stok" put the importable rows on a sheet
  // named "Template" / "Untuk import" (see IMPORT_SHEET_NAMES in
  // importTemplates.ts) — in the export it is not the first sheet. Any other
  // workbook is read from its first sheet, as before.
  const sheetName = preferredSheet(wb.SheetNames);
  if (!sheetName) throw new Error('File Excel tidak punya sheet apa pun.');
  const ws = wb.Sheets[sheetName];
  if (!ws) throw new Error(`Sheet "${sheetName}" tidak bisa dibaca.`);

  // Comma-delimited: parseCsv detects the delimiter, but emitting the one it
  // treats as canonical keeps quoting rules simple.
  return XLSX.utils.sheet_to_csv(ws, { FS: ',', blankrows: false });
}

const IMPORT_SHEETS = ['template', 'untuk import'];
const preferredSheet = (names: string[]): string | undefined =>
  names.find((n) => IMPORT_SHEETS.includes(n.trim().toLowerCase())) ?? names[0];

/** Name of the sheet an import reads, for telling the operator what was read. */
export async function firstSheetName(file: File): Promise<string | null> {
  if (!isExcelFile(file)) return null;
  const XLSX = await loadXlsx();
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', bookSheets: true });
  return preferredSheet(wb.SheetNames) ?? null;
}
